import { useCanAccess, useGetList, useTranslate } from "ra-core";
import {
  AlertTriangle,
  CalendarClock,
  Clock,
  FolderKanban,
  type LucideIcon,
} from "lucide-react";
import { Link } from "react-router";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

import type { TimeEntry } from "../types";
import { reportsPath } from "../reports/reportTabs";
import { entrySeconds, formatDuration } from "../workItems/timeFormat";

const pad = (n: number) => String(n).padStart(2, "0");
const iso = (d: Date) =>
  `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// Et nøkkeltall-kort med nøytralt ikon (analysen v2: fargede fliser gjorde
// Oversikt urolig). `warn` farger bare tallet rødt, for eksempel forfalte
// oppgaver over null. Med `to` blir kortet en klikkbar snarvei.
const KpiCard = ({
  label,
  value,
  icon: Icon,
  warn = false,
  to,
  loading = false,
  error = false,
}: {
  label: string;
  value: string;
  icon: LucideIcon;
  warn?: boolean;
  to?: string;
  loading?: boolean;
  error?: boolean;
}) => {
  const card = (
    <Card
      className={cn(
        "py-0 transition-colors",
        to && "hover:border-primary/40 hover:bg-muted/40",
      )}
    >
      <CardContent className="flex flex-col items-start gap-2 p-3 sm:flex-row sm:items-center sm:gap-4 sm:p-4">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted text-muted-foreground">
          <Icon className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <div className="min-h-8 text-xs leading-4 text-muted-foreground">
            {label}
          </div>
          <div
            className={cn(
              "mt-0.5 text-2xl font-semibold tabular-nums",
              warn && "text-destructive",
            )}
          >
            {error ? (
              <span className="text-sm text-destructive">Kunne ikke laste</span>
            ) : loading ? (
              <Skeleton className="h-7 w-16" />
            ) : (
              value
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
  return to ? (
    <Link to={to} className="block no-underline">
      {card}
    </Link>
  ) : (
    card
  );
};

// Nøkkeltall øverst på Oversikt: timer denne uken, aktive prosjekter, forfalte
// oppgaver og oppgaver som forfaller denne uken. MRR vises ikke her (Mimin
// 1.5): løpende avtaler er ikke fakturert omsetning, og kortet er fjernet.
export const DashboardKpis = () => {
  const translate = useTranslate();
  // Rapporter er for administrator; medarbeidere får kortet uten lenke
  // (PRD US12.5).
  const { canAccess: isAdmin } = useCanAccess({
    resource: "sales",
    action: "list",
  });

  const today = new Date();
  const day = (today.getDay() + 6) % 7; // mandag = 0
  const monday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate() - day,
  );
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const todayIso = iso(today);

  const {
    data: weekEntries = [],
    isPending: weekPending,
    isError: weekError,
  } = useGetList<TimeEntry>("time_entries", {
    filter: { "started_at@gte": `${iso(monday)}T00:00:00` },
    pagination: { page: 1, perPage: 5000 },
  });
  const weekSeconds = weekEntries.reduce(
    (sum, e) => sum + entrySeconds(e.started_at, e.ended_at),
    0,
  );

  const {
    total: activeProjects,
    isPending: projectsPending,
    isError: projectsError,
  } = useGetList("projects", {
    filter: { "archived_at@is": null },
    pagination: { page: 1, perPage: 1 },
  });

  const {
    total: overdue,
    isPending: overduePending,
    isError: overdueError,
  } = useGetList("work_items", {
    filter: { "due_date@lt": todayIso, "completed_at@is": null },
    pagination: { page: 1, perPage: 1 },
  });

  const {
    total: dueThisWeek,
    isPending: duePending,
    isError: dueError,
  } = useGetList("work_items", {
    filter: {
      "due_date@gte": todayIso,
      "due_date@lte": iso(sunday),
      "completed_at@is": null,
    },
    pagination: { page: 1, perPage: 1 },
  });

  return (
    <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
      <KpiCard
        label={translate("crm.dashboard.kpi.hours_this_week", {
          _: "Timer denne uken",
        })}
        value={formatDuration(weekSeconds)}
        loading={weekPending}
        error={weekError}
        icon={Clock}
        to={isAdmin ? reportsPath("time") : undefined}
      />
      <KpiCard
        label={translate("crm.dashboard.kpi.active_projects", {
          _: "Aktive prosjekter",
        })}
        value={String(activeProjects ?? 0)}
        loading={projectsPending}
        error={projectsError}
        icon={FolderKanban}
        to="/projects"
      />
      <KpiCard
        label={translate("crm.dashboard.kpi.overdue_tasks", {
          _: "Forfalte oppgaver",
        })}
        value={String(overdue ?? 0)}
        loading={overduePending}
        error={overdueError}
        icon={AlertTriangle}
        warn={(overdue ?? 0) > 0}
        to="/work_items"
      />
      <KpiCard
        label={translate("crm.dashboard.kpi.due_this_week", {
          _: "Forfaller denne uken",
        })}
        value={String(dueThisWeek ?? 0)}
        loading={duePending}
        error={dueError}
        icon={CalendarClock}
        to="/work_items"
      />
    </div>
  );
};
